import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactJson from 'react18-json-view';
import { strings } from '/imports/strings';
import { Dict } from '/imports/types';
import { defer, formatDateTime, objectKeys } from '/imports/utils';
import { Input } from '/imports/ui/Input';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { Catalog } from '/imports/ui/Catalog';
import { Table } from '/imports/ui/Table';
import { listLogs, ListLogsArg, Log, logData } from '/imports/api/logs';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';

export const LogsPage = () => {
  const [initializing, setInitializing] = useState(true);
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogs, setExpandedLogs] = useState<Dict<boolean>>({});
  const [levelFilters, setLevelFilters] = useState<Dict<boolean>>({ warning: true, error: true, critical: true });
  const levels = () => objectKeys(levelFilters).filter((k) => levelFilters[k]);
  const [arg, setArg] = useState<ListLogsArg>({ limit: 200, levels: levels() });
  const pollRef = useRef<ReturnType<typeof setInterval>>(null);
  const fetchLogs = useCallback(async (a: ListLogsArg) => {
    setLoading(true);
    const result = await listLogs.call(a);
    setLogs(result);
    setLoading(false);
  }, []);
  useEffect(() => {
    fetchLogs(arg);
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchLogs(arg), 10000);
    return () => { pollRef.current && clearInterval(pollRef.current); };
  }, [arg]);
  useEffect(() => {
    !initializing && setArg({ ...arg, levels: levels() });
  }, [levelFilters]);
  useEffect(() => {
    initializing && !loading && setInitializing(false);
  }, [loading]);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    !initializing && defer(() => setInitialized(true));
  }, [initializing]);
  const expandLog = async (log: Log) => {
    log.data = await logData.call(log._id);
    setExpandedLogs({ ...expandedLogs, [log._id]: !expandedLogs[log._id] });
  };
  return (
    <Page className={`page${!initialized ? ' initializing' : ''}`}>
      <PageHeading
        left={<h1>{strings.Logs()}</h1>}
      />
      <PageBody>
        <Catalog>
          <div className="filters flex flex-col gap-4">
            <div className="search-filters flex w-full gap-4">
              <Input onChange={(tags) => setArg({ ...arg, tags: tags?.split(',') || undefined })} placeholder={strings.Tags()} value={arg.tags?.join(',')} />
              {/* <Input onChange={(search) => setArg({ ...arg, search })} placeholder={strings.Search()} value={arg.search} /> */}
            </div>
            <div className="level-filters flex gap-4">
              <LevelFilter level="trace" label={strings.Trace()} enabled={levelFilters.trace} onClick={() => setLevelFilters({ ...levelFilters, trace: !levelFilters.trace })} />
              <LevelFilter level="info" label={strings.Info()} enabled={levelFilters.info} onClick={() => setLevelFilters({ ...levelFilters, info: !levelFilters.info })} />
              <LevelFilter level="warning" label={strings.Warning()} enabled={levelFilters.warning} onClick={() => setLevelFilters({ ...levelFilters, warning: !levelFilters.warning })} />
              <LevelFilter level="error" label={strings.Error()} enabled={levelFilters.error} onClick={() => setLevelFilters({ ...levelFilters, error: !levelFilters.error })} />
              <LevelFilter level="critical" label={strings.Critical()} enabled={levelFilters.critical} onClick={() => setLevelFilters({ ...levelFilters, critical: !levelFilters.critical })} />
            </div>
          </div>
          <div>
          </div>
          <Table>
            <table>
              <tbody>
                <tr>
                  <td>
                    {
                      loading && !logs.length ?
                        <LoadingFill />
                        :
                        logs.map((log) => {
                          return (
                            <LogEntry key={log._id} log={log} expanded={!!expandedLogs[log._id]} onExpand={() => expandLog(log)} />
                          );
                        })
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          </Table>
        </Catalog>
      </PageBody>
    </Page>
  );
};

const LogEntry = React.memo(function LogEntry(props: { log: Log, expanded: boolean, onExpand: () => void }) {
  const { log, expanded, onExpand } = props;
  return (
    <div className="border-b border-mid [&_.meta]:text-[0.85rem] [&_.expand]:cursor-pointer [&_.expand]:text-xs [&_.expand]:text-fg-3 [&_.critical_.meta]:font-bold [&_.critical_.meta]:text-gold [&_.error_.meta]:text-sell [&_.warning_.meta]:text-warn [&_.info_.meta]:text-fg-1 [&_.trace_.meta]:text-fg-3">
      <div className={log.level}>
        <span className="meta">{log.level.toUpperCase()} [{formatDateTime(log.timestamp)}]</span> <span className="expand" onClick={() => onExpand()}>({strings.Expand()})</span> {log.message}</div>
      {
        expanded ?
          (
            <div>
              <ReactJson src={log.data || {}} />
              <ReactJson src={log.extra || {}} />
            </div>
          )
          :
          null
      }
    </div>
  );
});

// Level chips follow the enamel-chip motif: low radius, hairline edge, a soft
// tint of the level's own color when enabled. The old hardcoded hexes had no
// dark-theme equivalent, so they read as bright blocks on obsidian.
const levelFilterClasses = [
  'inline-block min-w-16 cursor-pointer rounded-md border px-3 py-1.5 text-center text-[12px] font-semibold shadow-chip',
  '[&.critical]:border-viz-4/40 [&.critical]:text-viz-4 [&.critical.enabled]:bg-viz-4/15',
  '[&.error]:border-sell/40 [&.error]:text-sell [&.error.enabled]:bg-sell/15',
  '[&.warning]:border-warn/40 [&.warning]:text-warn [&.warning.enabled]:bg-warn/15',
  '[&.info]:border-info/40 [&.info]:text-info [&.info.enabled]:bg-info/15',
  '[&.trace]:border-soft [&.trace]:text-fg-3 [&.trace.enabled]:bg-page-alt',
].join(' ');

const LevelFilter = (props: { level: string, label: string, enabled: boolean, onClick: () => void }) => {
  const { level, label, enabled, onClick } = props;
  return (
    <span className={`${level}${enabled ? ' enabled' : ''} ${levelFilterClasses}`} onClick={() => onClick?.()}>
      {label}
    </span>
  );
};